<template>
  <nav class="tabbar" aria-label="main">
    <NuxtLink
      v-for="tab in tabs"
      :key="tab.key"
      :to="tab.path"
      class="tab"
      :class="{ on: tab.active, center: tab.center }"
      @click.capture="onTabClick($event, tab.key)"
    >
      <span v-if="tab.center" class="fab">
        <AppIcon :name="tab.icon" class="ico" />
      </span>
      <AppIcon v-else :name="tab.icon" class="ico" />
      <span>{{ tab.label }}</span>
    </NuxtLink>
  </nav>
</template>

<script setup lang="ts">
import type { AppIconName } from '~/assets/icons'

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const { walletFrom } = useWalletNav()
const { requireLogin } = useAuthGate()

type TabKey = 'home' | 'promo' | 'wallet' | 'earn' | 'me'

const tabDefs: {
  key: TabKey
  to: string
  labelKey: string
  icon: AppIconName
  center?: boolean
}[] = [
  { key: 'home', to: '/', labelKey: 'common.home', icon: 'tab-home' },
  { key: 'promo', to: '/promo', labelKey: 'common.promo', icon: 'tab-promo' },
  {
    key: 'wallet',
    to: '/wallet',
    labelKey: 'common.wallet',
    icon: 'tab-wallet',
    center: true,
  },
  { key: 'earn', to: '/earn', labelKey: 'common.earn', icon: 'tab-refer' },
  { key: 'me', to: '/me', labelKey: 'common.me', icon: 'tab-me' },
]

const matchTab = (key: TabKey, path: string) => {
  if (key === 'home') {
    const home = localePath('/')
    return path === home || path === `${home}/`
  }
  const target = localePath(tabDefs.find(d => d.key === key)!.to)
  return path === target || path.startsWith(`${target}/`)
}

const tabs = computed(() =>
  tabDefs.map(item => ({
    key: item.key,
    path: localePath(item.to),
    label: t(item.labelKey),
    icon: item.icon,
    center: !!item.center,
    active: matchTab(item.key, route.path),
  })),
)

/** 底导进充值 = 根入口，清推入来源；赚钱/奖励需登录后才可进入，完全拦截后自行跳转 */
const onTabClick = (e: MouseEvent, key: TabKey) => {
  if (key === 'wallet') walletFrom.value = null
  if (key !== 'earn' && key !== 'promo') return
  e.preventDefault()
  e.stopImmediatePropagation()
  requireLogin(() => {
    void navigateTo(localePath(key === 'earn' ? '/earn' : '/promo'))
  })
}
</script>

<style scoped lang="scss">
.tabbar {
  position: fixed;
  /* Chrome 85：避免 fixed + transform 合成层异常 */
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 50;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  background: var(--tabbar-bg);
  border-top: 1px solid var(--hairline);
  padding: 6px 4px calc(6px + env(safe-area-inset-bottom, 0px));
  -webkit-backdrop-filter: blur(12px);
  backdrop-filter: blur(12px);
  box-sizing: border-box;
  /* 旧内核上半透明底栏与内容交界易出现缝隙，加一层不透明兜底 */
  background-color: var(--bg);
  background-image: linear-gradient(var(--tabbar-bg), var(--tabbar-bg));
}

.tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  color: var(--muted);
  padding: 4px 0;
  position: relative;
  background: transparent;
  -webkit-tap-highlight-color: transparent;
  text-decoration: none;

  .ico {
    font-size: 22px;
  }

  > span:last-child {
    font-size: 10px;
    font-weight: 700;
    line-height: 1.2;
    background: transparent;
  }

  &.on {
    color: var(--primary);
  }

  &.center .fab {
    width: 48px;
    height: 48px;
    margin-top: -22px;
    border-radius: var(--r-lg);
    background: var(--fab-bg);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    box-shadow: var(--btn-glow);
    border: 3px solid var(--tabbar-bg);

    .ico {
      font-size: 24px;
    }
  }
}
</style>
